import type { PLATFORMS } from '@amzn/journeys-common';
import type { FieldValidator } from 'final-form';
import type { FieldRegistrationConfig } from 'hooks/form-spy/form-spy.types';
import { useSpyOnMultipleFields } from 'hooks/form-spy/useSpyOnMultipleFields';
import { useMemoCompare } from 'hooks/useMemoCompare';
import { useMemo } from 'react';
import { flattenObject } from 'utils';
import type { ValidateFlavorContentFn } from '../../campaign-configs/types/content-section.types';
import { useCampaignConfig } from '../../hooks/useCampaignConfig';
import { useFlavors } from '../../useFlavor';
import { validateCreativeName } from '../../validation';
import { getFlavorIndexFromFinalFormName, type TrafficCampaignFormValues } from '../generic.util';
import { useSharedVarsContext } from '../shared-variables/shared-variables-context';

type FlavorIndexToErrorCountMap = Record<number, number>;

type UseValidateAllFlavorContentProps = {
  cacheBuster?: string;
};

export const useValidateAllFlavorContent = ({ cacheBuster }: UseValidateAllFlavorContentProps): FlavorIndexToErrorCountMap => {
  const flavors = useFlavors();
  const { usedByPathToSharedVarKeyMap } = useSharedVarsContext();

  const numberOfUsedByPaths = useMemo(
    () => Object.keys(usedByPathToSharedVarKeyMap).length,
    [
      usedByPathToSharedVarKeyMap,
    ],
  );

  const config = useCampaignConfig();

  // Create a function that returns a validator for a specific field path
  const createContentValidator = useMemo(
    () => (flavorIndex: number) => {
      const {
        contentSection: { validateFlavorContent, contentConfiguration },
      } = config;

      // If no validateFlavorContent function is provided, return a no-op validator
      if (!validateFlavorContent) {
        return (): FieldValidator<unknown> => () => {};
      }

      type ContentType = Parameters<typeof validateFlavorContent>[0]['content'];
      return (): FieldValidator<ContentType> => (value, allValues) => {
        const formValues = allValues as TrafficCampaignFormValues;
        const platform = formValues.flavors?.[flavorIndex]?.scheduling?.platform as PLATFORMS;

        const validationResult = (validateFlavorContent as ValidateFlavorContentFn<ContentType, object>)({
          allFormValues: allValues,
          content: value,
          contentConfiguration,
          flavorIndex,
          platform,
        });
        return validationResult;
      };
    },
    [
      config,
    ],
  );

  // Make an array of the original indices of all active flavors
  const activeFlavorIndicesExpensive = useMemo(
    () =>
      flavors.reduce((acc, flavor, index) => {
        if (flavor.active) {
          acc.push(index);
        }
        return acc;
      }, [] as number[]),
    [
      flavors,
    ],
  );
  // Memo compare to avoid regenerating flavorContentRegistrationConfigs
  // This is relatively cheap
  const activeFlavorIndices = useMemoCompare(activeFlavorIndicesExpensive);

  const flavorContentRegistrationConfigs: FieldRegistrationConfig[] = useMemo(
    () =>
      activeFlavorIndices.reduce(
        (configsAcc, flavorIndex) => [
          // biome-ignore lint/performance/noAccumulatingSpread: legacy code
          ...configsAcc,
          {
            getValidator: createContentValidator(flavorIndex),
            path: `flavors[${flavorIndex}].content`,
          },
          {
            getValidator: () => validateCreativeName,
            path: `flavors[${flavorIndex}].creativeName`,
          },
        ],
        [] as FieldRegistrationConfig[],
      ),
    [
      createContentValidator,
      activeFlavorIndices,
    ],
  );

  const pathToFieldStateMap = useSpyOnMultipleFields({
    cacheBuster: `${numberOfUsedByPaths}-${cacheBuster}`,
    fieldRegistrationConfigs: flavorContentRegistrationConfigs,
    subscriptionKeys: [
      'error',
    ],
    validateOnRegister: true,
  });

  const flavorIndexToErrorCountMapExpensive: FlavorIndexToErrorCountMap = useMemo(() => {
    const newMap: FlavorIndexToErrorCountMap = {};

    Object.entries(pathToFieldStateMap).forEach(([contentPath, { error }]) => {
      if (!error) return;
      const flavorIndex = getFlavorIndexFromFinalFormName(contentPath);
      if (typeof error !== 'object') {
        newMap[flavorIndex] = (newMap[flavorIndex] || 0) + 1;
        return;
      }
      try {
        const flattenedErrors = flattenObject(error as Record<string, unknown>);
        newMap[flavorIndex] = Object.keys(flattenedErrors).length;
      } catch (e) {
        console.log('error', e);
        // Could not process error, but we want to count it
        newMap[flavorIndex] = 1;
      }
    });

    return newMap;
  }, [
    pathToFieldStateMap,
  ]);

  return useMemoCompare(flavorIndexToErrorCountMapExpensive);
};
